import {useTranslation} from 'react-i18next'
import {useEffect, useState} from 'react'
import {useAppDispatch} from '../../../../redux/hooks'
import {MemStats} from '../../../../redux/Slice/adminSlice'
import {ApexOptions} from 'apexcharts'
import ReactApexChart from 'react-apexcharts'
import Loader from 'app/custom/loader/Loader'

// MAIN COLOR PALETTE
const HORIZON_COLORS = [
  '#0A3641',
  '#124956',
  '#C78E25',
  '#E8C98E',
  '#ECEFEF',
  '#ACBBBE',
  '#768E95',
  '#40626B',
  '#2C525C',
  '#4B5A60',
  '#C3CED0',
  '#E0E7E8',
  '#1B1204',
  '#A2711C',
  '#7C949A',
]

// MUNICIPALITY SPECIAL PALETTE
const MUNICIPALITY_COLORS = [
  '#0A3641',
  '#124956',
  '#C78E25',
  '#E8C98E',
  '#768E95',
  '#40626B',
  '#2C525C',
  '#4B5A60',
  '#ACBBBE',
  '#A2711C',

  '#184F5B',
  '#245E68',
  '#326D76',
  '#407C84',
  '#4D8A91',
  '#5A989E',
  '#6AA5AA',
  '#7AB2B6',
  '#8BBFC2',
  '#9CCBCD',

  '#8B651C',
  '#9A711D',
  '#A97E22',
  '#B78A29',
  '#C49731',
  '#D0A43C',
  '#D9AF4B',
  '#E2BB5E',
  '#E8C873',
  '#EDD58B',

  '#173E47',
  '#20505A',
  '#29616A',
  '#33727A',
  '#3F838A',
  '#4C949A',
  '#5BA4A9',
  '#6CB3B7',
  '#7EC1C5',
  '#91CFD2',

  '#293B40',
  '#35484D',
  '#41555A',
  '#4E6267',
  '#5C7075',
  '#6B7E83',
  '#7A8C91',
  '#8A9B9F',
  '#9AABAE',
  '#ABBABD',

  '#4B3512',
  '#604617',
  '#76571C',
  '#8C6822',
  '#A17927',
  '#B78A2D',
  '#C89A37',
  '#D5AA49',
  '#DFBA60',
  '#E8CA7B',

  '#123F49',
  '#17505B',
  '#1E616C',
  '#28727D',
  '#34838D',
  '#42949E',
  '#52A5AE',
  '#64B5BD',
  '#78C5CC',
  '#8DD4DA',

  '#1B1204',
  '#30230C',
  '#463414',
  '#5C461C',
  '#725824',
  '#886A2D',
  '#9F7C37',
  '#B58E43',
  '#CAA052',
  '#DDB164',
]

// ONLY FEMALE USES PINK
const FEMALE_COLOR = '#F48FB1'

// PERCENTAGE CALCULATOR
const toPercent = (counts: number[]): number[] => {
  const total = counts.reduce((sum: number, value: number) => sum + value, 0)
  if (!total) return counts.map(() => 0)
  const exact = counts.map((value: number) => (value / total) * 100)
  const result = exact.map((value: number) => Math.floor(value))
  let remaining = 100 - result.reduce((sum: number, value: number) => sum + value, 0)
  const byFraction = exact
    .map((value: number, index: number) => ({
      index,
      fraction: value - Math.floor(value),
    }))
    .sort((a, b) => b.fraction - a.fraction)
  for (let i = 0; i < byFraction.length && remaining > 0; i++) {
    result[byFraction[i].index] += 1
    remaining--
  }
  return result
}

const MemberStatistic = () => {
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [statistics, setStatistics] = useState<any>([])
  const [loader, setLoader] = useState<boolean>(true)

  useEffect(() => {
    dispatch(MemStats()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setStatistics(res.payload)
        setLoader(false)
      } else setLoader(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // MUNICIPALITY
  const municipalityCounts = statistics?.municipality?.map((item: any) => Number(item.total)) || []
  const municipalityLabels =
    statistics?.municipality?.map((item: any) => {
      if (item.name === 'LABEL.OTHER') return t(item.name)
      return item.name || '-'
    }) || []

  const municipalitySeries = toPercent(municipalityCounts)

  // Dedicated large color palette
  const municipalityColors = municipalityLabels.map(
    (_: string, index: number) => MUNICIPALITY_COLORS[index % MUNICIPALITY_COLORS.length]
  )

  // Enough vertical space for 50+ municipalities
  const municipalityChartHeight = Math.max(430, municipalityLabels.length * 48 + 100)

  // GENDER
  const prefixSeries = statistics?.prefix?.map((item: any) => Number(item.total)) || []
  const prefixPercents = toPercent(prefixSeries)
  const prefixLabels = statistics?.prefix?.map((item: any) => t(item.name) || '-') || []

  const prefixColors =
    statistics?.prefix?.map((item: any, index: number) => {
      const rawName = String(item.name || '').toLowerCase()
      const translatedName = String(t(item.name) || '').toLowerCase()
      // Female ONLY = Pink
      if (
        ['female', 'femme', 'woman'].some((word) => `${rawName} ${translatedName}`.includes(word))
      )
        return FEMALE_COLOR
      return HORIZON_COLORS[index % HORIZON_COLORS.length]
    }) || []

  // MEMBERSHIP CATEGORY
  const categorySeries = statistics?.category?.map((item: any) => Number(item.total)) || []
  const categoryPercents = toPercent(categorySeries)
  const categoryLabels = statistics?.category?.map((item: any) => item.name || '-') || []
  const categoryColors = categoryLabels.map(
    (_: string, index: number) => HORIZON_COLORS[(index + 1) % HORIZON_COLORS.length]
  )

  // STATUS
  const statusSeries = statistics?.status?.map((item: any) => Number(item.total)) || []
  const statusPercents = toPercent(statusSeries)
  const statusLabels =
    statistics?.status?.map((item: any) => t('LABEL.' + item.name) || item.name || '-') || []

  // STATUS COLORS
  const getStatusColor = (status: string, index: number) => {
    const value = String(status || '')
      .trim()
      .toLowerCase()

    // Check inactive BEFORE active
    if (value.includes('inactive') || value.includes('disabled')) return '#768E95'

    // Active / Approved / Accepted
    if (['active', 'approved', 'accepted', 'valid', 'confirmed'].some((x) => value.includes(x)))
      return '#0A3641'

    // Pending
    if (['pending', 'waiting', 'processing', 'review'].some((x) => value.includes(x)))
      return '#C78E25'

    // Rejected
    if (value.includes('reject') || value.includes('cancel') || value.includes('declined'))
      return '#1B1204'

    // Draft / New
    if (value.includes('draft') || value.includes('new')) return '#E8C98E'

    // Completed
    if (value.includes('complete') || value.includes('finished')) return '#40626B'

    return HORIZON_COLORS[index % HORIZON_COLORS.length]
  }

  const statusColors =
    statistics?.status?.map((item: any, index: number) => getStatusColor(item.name, index)) || []

  // THEMATICS
  const thematicData = statistics?.thematics || statistics?.thematic || []
  const thematicSeries = thematicData.map((item: any) => Number(item.count ?? item.total ?? 0))
  const thematicPercents = toPercent(thematicSeries)
  const thematicLabels = thematicData.map((item: any) => item.name || '-')
  const thematicColors = thematicLabels.map(
    (_: string, index: number) => HORIZON_COLORS[index % HORIZON_COLORS.length]
  )

  // TITLE
  const chartTitle = (text: string): ApexOptions['title'] => ({
    text,
    align: 'left',
    margin: 15,
    offsetX: 5,
    offsetY: 5,

    style: {
      fontSize: '17px',
      fontWeight: 700,
      color: '#0A3641',
    },
  })

  // PIE CHART CREATOR
  const createPieChartOptions = (
    title: string,
    labels: string[],
    percents: number[],
    colors: string[]
  ): ApexOptions => ({
    chart: {
      type: 'pie',
      height: 370,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 700,
      },
      fontFamily: 'inherit',
    },
    title: chartTitle(title),
    labels,
    colors,
    stroke: {
      show: true,
      width: 2,
      colors: ['#FFFFFF'],
    },
    legend: {
      show: true,
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      labels: {
        colors: '#4B5A60',
        useSeriesColors: false,
      },
      markers: {
        width: 10,
        height: 10,
        radius: 10,
      },
      itemMargin: {
        horizontal: 8,
        vertical: 5,
      },
    },

    dataLabels: {
      enabled: true,
      formatter: function (_val: number, opts: any) {
        const index = opts?.seriesIndex ?? 0
        return `${percents[index] ?? 0}%`
      },

      dropShadow: {
        enabled: false,
      },

      style: {
        fontSize: '12px',
        fontWeight: '700',
        colors: ['#FFFFFF'],
      },
    },

    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const index = opts?.seriesIndex ?? 0
          const percent = percents[index] ?? 0
          return `${val} (${percent}%)`
        },
      },
    },

    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.05,
        },
      },

      active: {
        filter: {
          type: 'darken',
          value: 0.05,
        },
      },
    },

    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: 340,
          },
          legend: {
            position: 'bottom',
          },
        },
      },
    ],
  })

  // MUNICIPALITY HORIZONTAL BAR CHART
  const municipalityOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: municipalityChartHeight,
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 700,
      },
      fontFamily: 'inherit',
    },

    title: chartTitle(t('LABEL.MEMBERS_BY_COMMUNE')),

    colors: municipalityColors,

    plotOptions: {
      bar: {
        horizontal: true,
        distributed: true,
        borderRadius: 4,
        barHeight: '60%',
        dataLabels: {
          position: 'top',
        },
      },
    },

    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      offsetX: 8,

      formatter: function (val: number) {
        return `${val}%`
      },

      style: {
        fontSize: '12px',
        fontWeight: '700',
        colors: ['#0A3641'],
      },
    },

    legend: {
      show: false,
    },

    xaxis: {
      categories: municipalityLabels,
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: function (val: string) {
          return `${Math.round(Number(val))}%`
        },

        style: {
          fontSize: '11px',
          colors: '#768E95',
        },
      },

      axisBorder: {
        show: false,
      },

      axisTicks: {
        show: false,
      },
    },

    yaxis: {
      labels: {
        show: true,
        maxWidth: 350,
        style: {
          fontSize: '12px',
          fontWeight: 500,
          colors: '#0A3641',
        },

        formatter: function (value: number) {
          return String(value)
        },
      },
    },

    grid: {
      borderColor: '#E0E7E8',
      strokeDashArray: 4,
      padding: {
        left: 15,
        right: 50,
        top: 5,
        bottom: 5,
      },
    },

    tooltip: {
      y: {
        formatter: function (val: number, opts: any) {
          const index = opts?.dataPointIndex ?? 0
          const count = municipalityCounts[index] ?? 0
          return `${count} members (${val}%)`
        },
      },
    },

    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.05,
        },
      },

      active: {
        filter: {
          type: 'darken',
          value: 0.05,
        },
      },
    },

    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            height: municipalityChartHeight,
          },

          yaxis: {
            labels: {
              maxWidth: 220,
              style: {
                fontSize: '10px',
              },
            },
          },

          dataLabels: {
            enabled: true,
            style: {
              fontSize: '10px',
            },
          },
        },
      },
    ],
  }

  // CHART OPTIONS
  const prefixOptions = createPieChartOptions(
    t('LABEL.GENDER'),
    prefixLabels,
    prefixPercents,
    prefixColors
  )

  const categoryOptions = createPieChartOptions(
    t('LABEL.MEMBERSHIP'),
    categoryLabels,
    categoryPercents,
    categoryColors
  )

  const statusOptions = createPieChartOptions(
    t('LABEL.STATUS'),
    statusLabels,
    statusPercents,
    statusColors
  )

  const thematicOptions = createPieChartOptions(
    t('LABEL.THEMATIC_COMMUNITIES'),
    thematicLabels,
    thematicPercents,
    thematicColors
  )

  if (loader) return <Loader />

  return (
    <>
      {/* MUNICIPALITY GRAPH */}
      <div className='row g-5 mb-5'>
        <div className='col-12'>
          <div className='card shadow-sm h-100'>
            <div
              className='card-body'
              style={{
                overflow: 'visible',
              }}
            >
              <ReactApexChart
                options={municipalityOptions}
                series={[
                  {
                    name: t('LABEL.MUNICIPALITY'),
                    data: municipalitySeries,
                  },
                ]}
                type='bar'
                height={municipalityChartHeight}
                width='100%'
              />
            </div>
          </div>
        </div>
      </div>

      <div className='row g-5 mb-5'>
        {/* GENDER GRAPH */}
        <div className='col-12 col-lg-6'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={prefixOptions}
                series={prefixSeries}
                type='pie'
                height={370}
                width='100%'
              />
            </div>
          </div>
        </div>

        {/* MEMBERSHIP GRAPH */}
        <div className='col-12 col-lg-6'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={categoryOptions}
                series={categorySeries}
                type='pie'
                height={370}
                width='100%'
              />
            </div>
          </div>
        </div>
      </div>

      {/* STATUS GRAPH */}
      <div className='row g-5 mb-5'>
        <div className='col-12 col-lg-6'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={statusOptions}
                series={statusSeries}
                type='pie'
                height={390}
                width='100%'
              />
            </div>
          </div>
        </div>

        <div className='col-12 col-lg-6'>
          <div className='card shadow-sm h-100'>
            <div className='card-body'>
              <ReactApexChart
                options={thematicOptions}
                series={thematicSeries}
                type='pie'
                height={390}
                width='100%'
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MemberStatistic
